import { ControllerFlowAPI, VisitorLogger } from '@wix/yoshi-flow-editor';
import {
  bookingsCalendarClick,
  bookingsCalendarTimePickerLoad,
  bookingsCalendarDatePickerLoad,
  bookingsCalendarErrorMessages,
  bookingsCalendarBookingDetailsLoad,
  bookingsContactInfoSaveSuccess,
  bookingsPaymentMethodSelectionNextClicked,
  bookingsLoginUserAccountAppointmentsRescheduleSuccess,
  bookingsCalendarPageLoaded,
} from '@wix/bi-logger-wixboost-ugc/v2';
import { CalendarState } from '../../components/BookingCalendar/controller';
import { Preset, Preference } from '../../types/types';
import { CalendarContext, LanguageSettings } from '../context/contextFactory';
import {
  BookingsQueryParams,
  getUrlQueryParamValue,
} from '@wix/bookings-catalog-calendar-viewer-utils';
import { Service } from '@wix/ambassador-bookings-services-v2-service/types';
import { isServiceOfferedAsPricingPlan } from '@wix/bookings-calendar-catalog-viewer-mapper';

export interface CalendarBiLogger extends VisitorLogger {
  update: (state: CalendarState, context: CalendarContext) => void;
  bookingsCalendarClick: typeof bookingsCalendarClick;
  bookingsCalendarPageLoaded: typeof bookingsCalendarPageLoaded;
  bookingsCalendarTimePickerLoad: typeof bookingsCalendarTimePickerLoad;
  bookingsCalendarDatePickerLoad: typeof bookingsCalendarDatePickerLoad;
  bookingsCalendarErrorMessages: typeof bookingsCalendarErrorMessages;
  bookingsCalendarBookingDetailsLoad: typeof bookingsCalendarBookingDetailsLoad;
  bookingsContactInfoSaveSuccess: typeof bookingsContactInfoSaveSuccess;
  bookingsPaymentMethodSelectionNextClicked: typeof bookingsPaymentMethodSelectionNextClicked;
  bookingsLoginUserAccountAppointmentsRescheduleSuccess: typeof bookingsLoginUserAccountAppointmentsRescheduleSuccess;
}

type CalendarContextWithoutBiLogger = Omit<CalendarContext, 'biLogger'>;
export function createCalendarBiLogger(
  flowAPI: ControllerFlowAPI,
  initialState: CalendarState,
  settings: any,
  settingsParams: any,
  preset: Preset,
  context: CalendarContextWithoutBiLogger,
  languageSettings: LanguageSettings,
): CalendarBiLogger {
  const { bi: viewerBiLogger } = flowAPI;

  const computeExtraInfo = (
    state: CalendarState,
    ctx: CalendarContextWithoutBiLogger,
  ) => {
    const calendarSelections = ctx.calendarSelections;
    if (!calendarSelections) {
      return undefined;
    }

    const selectedStaffPreference = state.selectedBookingPreferences?.find(
      (pref) => pref.key === Preference.STAFF_MEMBER,
    );

    const selectedStaffIdFromPreference = selectedStaffPreference?.value;

    const extra = calendarSelections.services.map(
      ({ id: serviceId, resources }) => {
        const staffFromCalendarSelections = resources?.[0];
        const staffMemberIds =
          staffFromCalendarSelections !== undefined
            ? [staffFromCalendarSelections]
            : selectedStaffIdFromPreference !== undefined
            ? [selectedStaffIdFromPreference]
            : [];

        return {
          serviceId,
          staffMemberIds,
        };
      },
    );
    return JSON.stringify(extra);
  };

  const getServiceLocationIds = (service: Service) => {
    return service?.locations
      ?.map((location) => location?.id)
      .filter((locationId) => locationId !== undefined);
  };

  const getServiceProperties = (state: CalendarState) => {
    const serviceProperties = state.servicesInView.reduce(
      (previousServiceProperties, service) => {
        return {
          ...previousServiceProperties,
          [service.id!]: {
            serviceId: service?.id,
            serviceType: service?.type,
            locationIds: getServiceLocationIds(service),
            staffMemberIds: service.staffMemberIds,
            isOfferedAsPricingPlan: isServiceOfferedAsPricingPlan(service),
            connectedSolutions: [],
          },
        };
      },
      {},
    );
    return JSON.stringify(serviceProperties);
  };

  const getSelectedFilters = (state: CalendarState) => {
    const service = state.servicesInView?.[0];
    const PRICE_OPTION = state.serviceVariantsMap[service?.id!]?.options;
    return JSON.stringify({ ...state.filterOptions, PRICE_OPTION });
  };
  const getPriceType = (state: CalendarState) => {
    const service = state.servicesInView?.[0];
    return JSON.stringify(
      state.serviceVariantsMap[service?.id!]?.options?.values?.map(
        ({ type }) => type,
      ) || [],
    );
  };

  const mapStateToDefaultBiParams = (state: CalendarState) => {
    const service = state?.servicesInView[0];
    return {
      serviceType: service?.type,
      serviceId: service?.id,
      selectedTimezone: state?.selectedTimezone,
      errorMessage: JSON.stringify(state?.calendarErrors),
      serviceProperties: getServiceProperties(state),
      selectedFilters: getSelectedFilters(state),
      selectedDate: state?.selectedDate,
      selectedRange: JSON.stringify(state?.selectedRange),
      bookingId: state?.rescheduleBookingDetails?.id,
      priceType: getPriceType(state),
    };
  };

  const mapContextToDefaultBiParams = (ctx: CalendarContextWithoutBiLogger) => {
    const { calendarSelections } = ctx;

    if (!calendarSelections) {
      return {};
    }

    return {
      numberOfServices: calendarSelections.services.length,
    };
  };

  const updateDefaultBiParams = (
    state: CalendarState,
    ctx: CalendarContext,
  ) => {
    const contextDefaults = mapContextToDefaultBiParams(ctx);
    viewerBiLogger?.updateDefaults({
      ...mapStateToDefaultBiParams(state),
      ...contextDefaults,
      extraInfo: computeExtraInfo(state, ctx),
    });
  };

  viewerBiLogger?.updateDefaults({
    pageName: preset,
    referralInfo: getUrlQueryParamValue(
      flowAPI.controllerConfig.wixCodeApi,
      BookingsQueryParams.REFERRAL,
    ),
    businessProperties: JSON.stringify({
      language: languageSettings.language,
      countryCode: languageSettings.countryCode,
    }),
    ...mapStateToDefaultBiParams(initialState),
    ...mapContextToDefaultBiParams(context),
    extraInfo: computeExtraInfo(initialState, context),
    defaultDateAtFirstLoad: settings.get(settingsParams.initializeCalendarDate),
    layout: settings.get(settingsParams.calendarLayout),
  });

  return Object.assign(viewerBiLogger!, {
    update: updateDefaultBiParams,
  }) as CalendarBiLogger;
}
